export interface DrawRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where to draw the whole source image on the export canvas so it matches
 * exactly what the crop box shows (react-easy-crop, `objectFit="cover"`:
 * at zoom 1 the image's short side fills the box, then `zoom` scales it
 * around the box center and `crop` offsets it, both in box pixels).
 *
 * Used instead of react-easy-crop's `croppedAreaPixels`, which clamps the
 * area to the image bounds — so once a logo is zoomed out far enough to leave
 * padding, the library reports a plain square slice of the image and the
 * export stops matching the preview.
 */
export function computeLogoDrawRect({
  naturalWidth,
  naturalHeight,
  crop,
  zoom,
  boxSize,
  exportSize,
}: {
  naturalWidth: number;
  naturalHeight: number;
  crop: { x: number; y: number };
  zoom: number;
  boxSize: number;
  exportSize: number;
}): DrawRect {
  const coverScale = boxSize / Math.min(naturalWidth, naturalHeight);
  const scale = coverScale * zoom * (exportSize / boxSize);
  const width = naturalWidth * scale;
  const height = naturalHeight * scale;
  const cropScale = exportSize / boxSize;

  return {
    x: exportSize / 2 - width / 2 + crop.x * cropScale,
    y: exportSize / 2 - height / 2 + crop.y * cropScale,
    width,
    height,
  };
}

/**
 * Smallest zoom a transparent logo may use. Normally `preferredMinZoom`
 * (padding room for square-ish marks), but a wide/tall logo needs to go down
 * to the zoom at which its long side fits the box ("contain"), otherwise it
 * can never be shown whole.
 */
export function computeTransparentMinZoom(
  naturalWidth: number,
  naturalHeight: number,
  preferredMinZoom: number
): number {
  const containZoom =
    Math.min(naturalWidth, naturalHeight) /
    Math.max(naturalWidth, naturalHeight);
  return Math.min(preferredMinZoom, containZoom);
}

/** Share of the box a freely-placed logo must keep overlapping on each axis. */
const MIN_VISIBLE_FRACTION = 0.2;

/**
 * Keeps a pan offset (`crop`, in box pixels) within the allowed range.
 *
 * - `strict` (opaque images): the image never stops covering the box on an
 *   axis where it's larger, so no background edge can show; a smaller one
 *   stays fully inside.
 * - `free` (transparent logos, which get a background color anyway): move in
 *   any direction, as long as at least `MIN_VISIBLE_FRACTION` of the box on
 *   that axis still overlaps the image — so it can't be dragged out of view.
 *
 * Replaces react-easy-crop's `restrictPosition`, which pins any axis where the
 * image is smaller than the box to the center, making a zoomed-out logo
 * impossible to reposition.
 */
export function clampLogoCrop({
  naturalWidth,
  naturalHeight,
  crop,
  zoom,
  boxSize,
  mode,
}: {
  naturalWidth: number;
  naturalHeight: number;
  crop: { x: number; y: number };
  zoom: number;
  boxSize: number;
  mode: 'strict' | 'free';
}): { x: number; y: number } {
  const coverScale = boxSize / Math.min(naturalWidth, naturalHeight);
  const maxOffset = (imageSize: number) =>
    mode === 'strict'
      ? Math.abs(imageSize - boxSize) / 2
      : (imageSize + boxSize) / 2 - boxSize * MIN_VISIBLE_FRACTION;
  const maxX = maxOffset(naturalWidth * coverScale * zoom);
  const maxY = maxOffset(naturalHeight * coverScale * zoom);

  return {
    x: Math.min(maxX, Math.max(-maxX, crop.x)),
    y: Math.min(maxY, Math.max(-maxY, crop.y)),
  };
}
